import React, { useState, useEffect } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
} from '@tanstack/react-table'
import type { Table, SortingState, RowSelectionState, PaginationState } from '@tanstack/table-core'

import { useDocumments } from '../modules/documents/documents.hook'
import { useStore } from '../../application/store/store'
import { useDocumentUseCase } from '../../application/useCases/useDocuments'
import { Document } from '../../domain/documents.types'

export type UseDocumentsTableProps = {
  data: Document[]
  columns: any
}

interface UseDocumentTableHookProps {
  data: Document[]
  columns: any
  setIsDownloading: React.Dispatch<React.SetStateAction<boolean>>
}

interface UseDocumentTableHookReturn {
  tableInstance: Table<Document>
  inputPage: number | ''
  setInputPage: React.Dispatch<React.SetStateAction<number | ''>>
  NUMB_OF_ENTRIES: number[]
  rowSelection: RowSelectionState
  pagination: PaginationState
  setPagination: React.Dispatch<React.SetStateAction<PaginationState>>
  rowCount: number
  setRowCount: React.Dispatch<React.SetStateAction<number>>
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
  sorting: SortingState
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  searchInput: string | null
  setSearchInput: React.Dispatch<React.SetStateAction<string | null>>
  searchQuery: string | null
  setSearchQuery: React.Dispatch<React.SetStateAction<string | null>>
  isLoading: boolean
}

export const useDocumentTableHook = (
  props: UseDocumentTableHookProps
): UseDocumentTableHookReturn => {
  const { data, columns, setIsDownloading } = props

  const { setDocuments } = useStore()

  const { getDocumentListByPlantWithCount, getDocumentListByUserWithCount } = useDocumentUseCase()

  const { selectedPlant } = useDocumments()

  const NUMB_OF_ENTRIES: number[] = [25, 50, 100, 200] // rows per table

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  })
  const [searchInput, setSearchInput] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState<string | null>(null)

  const [rowCount, setRowCount] = useState<number>(0)
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  const [sorting, setSorting] = useState<SortingState>([{ id: 'displayName', desc: true }])
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const tableInstance = useReactTable({
    data,
    columns,
    getRowId: (original: Document) => original.sharepointId, // set unique row id based on sharepointId
    state: {
      pagination,
      sorting,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    enableSortingRemoval: false, // https://tanstack.com/table/latest/docs/api/features/sorting#enablesortingremoval
    onSortingChange: setSorting,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false,
  })

  const [inputPage, setInputPage] = React.useState<number | ''>(
    tableInstance.getState().pagination.pageIndex + 1
  )

  useEffect(() => {
    setInputPage(tableInstance.getState().pagination.pageIndex + 1)
  }, [tableInstance.getState().pagination.pageIndex])

  useEffect(() => {
    setIsDownloading(true)
    setIsLoading(true)
    const fetchDocuments = async () => {
      try {
        const response = await (selectedPlant
          ? getDocumentListByPlantWithCount(
              selectedPlant.code_centrale,
              pagination,
              searchQuery,
              sorting
            )
          : getDocumentListByUserWithCount(pagination, searchQuery, sorting))
        setRowSelection({})
        setDocuments(response.data)
        setRowCount(response.rowCount)
        setIsLoading(false)
        setIsDownloading(false)
      } catch (err) {
        console.error('Error fetching data:', err)
      }
    }
    fetchDocuments()
  }, [pagination, selectedPlant, searchQuery, sorting])

  return {
    tableInstance,
    inputPage,
    setInputPage,
    NUMB_OF_ENTRIES,
    rowSelection,
    pagination,
    setPagination,
    rowCount,
    setRowCount,
    setRowSelection,
    sorting,
    setSorting,
    searchInput,
    setSearchInput,
    searchQuery,
    setSearchQuery,
    isLoading,
  }
}
